export const enUSSettings = {
  title: 'Settings',
  profile: {
    title: 'Profile',
    name: 'Name',
    email: 'Email',
  },
  preferences: {
    title: 'Preferences',
    language: 'Language',
    languageSaved: 'Language updated',
    languageSaveError: 'Failed to save language',
  },
  management: {
    title: 'Management',
    categories: 'Categories',
    categoriesDescription: 'Organize your expenses by categories',
    accounts: 'Accounts',
    accountsDescription: 'Manage your bank accounts and cards',
  },
  household: {
    title: 'Household',
    name: 'Name',
    members: 'Members',
    manager: 'Manager',
    member: 'Member',
    saved: 'Household updated',
    saveError: 'Failed to save',
  },
  security: {
    title: 'Security',
    pinLock: 'PIN lock',
    pinLockDescription: 'Require a 4-digit PIN to unlock the app',
    enablePin: 'Enable PIN',
    disablePin: 'Disable PIN',
    changePin: 'Change PIN',
    enterPasswordToEnable: 'Enter your password to enable PIN lock.',
    password: 'Password',
    passwordRequired: 'Password is required',
    enterNewPin: 'New PIN',
    choosePin: 'Choose a 4-digit PIN.',
    chooseNewPin: 'Choose a new 4-digit PIN.',
    confirmPin: 'Confirm PIN',
    confirmPinDescription: 'Enter the PIN again to confirm.',
    enterCurrentPin: 'Current PIN',
    enterCurrentPinDescription: 'Enter your current PIN to continue.',
    enterPinToDisable: 'Enter your current PIN to disable the lock.',
    currentPin: 'Current PIN',
    newPin: 'New PIN',
    pinMustBe4Digits: 'PIN must be exactly 4 digits',
    pinsDontMatch: 'PINs do not match',
    pinEnabled: 'PIN enabled',
    pinDisabled: 'PIN disabled',
    pinChanged: 'PIN changed',
    pinError: 'Failed to process PIN',
    next: 'Next',
    enable: 'Enable',
    disable: 'Disable',
    change: 'Change',
    cancel: 'Cancel',
    saving: 'Saving...',
    idleTimeout: 'Auto-lock after',
    idleTimeoutDescription: 'Lock the app after this period of inactivity',
    idleTimeout1: '1 minute',
    idleTimeout5: '5 minutes',
    idleTimeout15: '15 minutes',
    idleTimeout30: '30 minutes',
    lockOnHide: 'Lock when tab is hidden',
    lockOnHideDescription: 'Lock the app when you switch to another tab',
    lockScreen: {
      title: 'App locked',
      subtitle: 'Enter your PIN to continue',
      pinLabel: 'Unlock PIN',
      wrongPin: 'Wrong PIN (attempt {{attempts}} of 5)',
      attemptsRemaining: '{{count}} attempts remaining',
    },
  },
  integrations: {
    title: 'Integrations',
    googleDrive: 'Google Drive',
    connectDescription: 'Connect Google Drive to upload receipts for your expenses',
    connect: 'Connect Google Drive',
    disconnect: 'Disconnect',
    connected: 'Connected',
    notConnected: 'Not connected',
    connectedSince: 'Connected {{date}}',
    disconnectConfirmTitle: 'Disconnect Google Drive?',
    disconnectConfirmDescription: 'Existing receipts already saved in Drive will not be deleted, but you will not be able to upload new receipts until you reconnect.',
    disconnectConfirmAction: 'Disconnect',
    disconnectConfirmCancel: 'Cancel',
    toastConnected: 'Google Drive connected',
    toastDisconnected: 'Google Drive disconnected',
    toastErrorDenied: 'Google Drive access was denied',
    toastErrorFailed: 'Failed to connect Google Drive',
    toastDisconnectError: 'Failed to disconnect Google Drive',
    rootFolder: 'Receipt folder',
    rootFolderNotSet: 'No folder selected',
    selectRootFolder: 'Select folder',
    changeRootFolder: 'Change folder',
    rootFolderSelected: 'Folder "{{name}}" selected',
    rootFolderError: 'Failed to select folder',
    itemPictureFolder: 'List item picture folder',
    itemPictureFolderNotSet: 'No folder selected',
    selectItemPictureFolder: 'Select folder',
    changeItemPictureFolder: 'Change folder',
    itemPictureFolderSelected: 'Folder "{{name}}" selected',
    itemPictureFolderError: 'Failed to select folder',
    googleCalendar: 'Google Calendar',
    calendarDescription: 'Choose which Google Calendar backs your family events',
    calendarConnectHint: 'Connect Google Drive above to enable calendar selection',
    selectedCalendar: 'Selected calendar',
    calendarNotSet: 'No calendar selected',
    selectCalendar: 'Select calendar',
    changeCalendar: 'Change calendar',
    calendarPickerPlaceholder: 'Choose a calendar…',
    calendarSelected: 'Calendar "{{name}}" selected',
    calendarSelectError: 'Failed to select calendar',
    calendarListError: 'Failed to load calendars — try reconnecting Google',
  },
  invites: {
    title: 'Invites',
    description: 'Invite people to join your household',
    createInvite: 'Create invite',
    createInviteWithEmail: 'Create & send email',
    emailOptional: 'Email (optional)',
    emailPlaceholder: 'invitee@example.com',
    linkCopied: 'Invite link copied!',
    copyLink: 'Copy link',
    sent: 'Invite created',
    sentWithEmail: 'Invite sent to {{email}}',
    revoke: 'Revoke',
    revokeConfirm: 'Revoke this invite?',
    revoked: 'Invite revoked',
    noInvites: 'No invites yet',
    status: {
      active: 'Active',
      used: 'Used',
      expired: 'Expired',
    },
    expiresIn: 'Expires {{date}}',
    usedBy: 'Used',
    createdBy: 'by {{name}}',
    error: 'Failed to create invite',
    revokeError: 'Failed to revoke invite',
  },
  mural: {
    title: 'Mural',
    groceriesListLabel: 'Mural groceries list',
    groceriesListHelp:
      'The list shown by the Mercado button on paired screens, and where items added on the Mural go.',
    automatic: 'Automatic',
    automaticWithList: '({{name}})',
    noLists: 'No lists yet.',
    createList: 'Create one in Lists',
    saved: 'Mural list updated',
    saveError: 'Failed to save the Mural list',
    loadError: 'Could not load Mural settings',
    retry: 'Retry',
  },
  devices: {
    title: 'Devices',
    description: 'Pair a Mural screen by entering the code it shows.',
    codeLabel: 'Pairing code',
    codePlaceholder: 'ABCD-EFGH',
    approve: 'Pair device',
    approved: 'Device paired',
    codeRequired: 'Enter the code shown on the screen',
    errorNotFound: 'No pairing found for that code — check the screen and try again',
    errorExpired: 'That code has expired — refresh the screen for a new one',
    errorUsed: 'That code was already used',
    errorRateLimited: 'Too many attempts — wait a minute and try again',
    errorGeneric: 'Failed to pair device',
    listTitle: 'Paired devices',
    noDevices: 'No devices paired yet',
    lastSeen: 'Last seen {{when}}',
    neverSeen: 'Never seen',
    pairedBy: 'Paired by {{name}} on {{date}}',
    pairedOn: 'Paired on {{date}}',
    listError: 'Could not load devices',
    retry: 'Retry',
    revoke: 'Revoke',
    revokeAriaLabel: 'Revoke {{name}}',
    revokeConfirmTitle: 'Revoke {{name}}?',
    revokeConfirmDescription:
      'The screen will stop showing household data right away and must be paired again to come back.',
    revokeConfirmCancel: 'Cancel',
    revoked: 'Device revoked',
    revokeError: 'Failed to revoke device',
  },
  notifications: {
    title: 'Notifications',
    pushNotifications: 'Push notifications',
    pushDescription: 'Receive browser notifications when bills are due or overdue',
    enable: 'Enable notifications',
    disable: 'Disable notifications',
    enabled: 'Notifications are enabled',
    disabled: 'Notifications are disabled',
    permissionDenied: 'Notifications are blocked by your browser. Enable them in your browser settings.',
    notSupported: 'Push notifications are not supported in this browser.',
    iosNote: 'On iOS, push notifications require iOS 16.4+ and the app must be added to your home screen.',
    enableSuccess: 'Notifications enabled',
    enableError: 'Failed to enable notifications',
    disableSuccess: 'Notifications disabled',
    disableError: 'Failed to disable notifications',
  },
} as const;
